import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';
import { ConversationalCRMInterface } from '../../components/crm/ConversationalCRMInterface';
import { conversationalCRMService } from '../../services/conversationalCRMService';

export const ConversationalCRMPage: React.FC = () => {
  const { t } = useLanguage();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSuggestions = async () => {
      try {
        const data = await conversationalCRMService.getSuggestions();
        setSuggestions(data);
      } catch (error) {
        console.error('Error loading suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSuggestions();
  }, []);

  const handleContactUpdate = () => {
    // This could trigger a refresh of contact lists or notifications
      };

  const handleInteractionUpdate = () => {
    // This could trigger a refresh of interaction lists or notifications
      };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversational CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Conversational Interface */}
          <div className="lg:col-span-2">
            <ConversationalCRMInterface
              onContactUpdate={handleContactUpdate}
              onInteractionUpdate={handleInteractionUpdate}
            />
          </div>

          {/* Help Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                How to Use
              </h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">
                    Contact Management
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {t('crm.conversational.examples.addContact')}</li>
                    <li>• {t('crm.conversational.examples.updateContact')}</li>
                    <li>• {t('crm.conversational.examples.searchContact')}</li>
                    <li>• {t('crm.conversational.examples.deleteContact')}</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-2">
                    Interaction Management
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {t('crm.conversational.examples.addInteraction')}</li>
                    <li>• {t('crm.conversational.examples.addCall')}</li>
                    <li>• {t('crm.conversational.examples.updateInteraction')}</li>
                    <li>• {t('crm.conversational.examples.searchInteraction')}</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">
                    💡 Tips
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Be specific with names and email addresses</li>
                    <li>• Include context like &quot;meeting&quot;, &quot;call&quot;, or &quot;email&quot;</li>
                    <li>• Mention outcomes like &quot;positive&quot;, &quot;negative&quot;, or &quot;neutral&quot;</li>
                    <li>• Use natural language - no need for exact commands</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            {suggestions.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-2">
                  {suggestions.slice(0, 5).map((suggestion, index) => (
                    <button
                      key={index}
                      className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                      onClick={() => {
                        // This would populate the input field
                                              }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Features Overview */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Contact Management
              </h3>
              <p className="text-gray-600 text-sm">
                Add, update, search, and delete contacts using natural language
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Interaction Tracking
              </h3>
              <p className="text-gray-600 text-sm">
                Log meetings, calls, emails, and other interactions conversationally
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Smart Processing
              </h3>
              <p className="text-gray-600 text-sm">
                AI-powered intent recognition and data extraction from natural language
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
